// Illustrated worksheet template — a shared, print/PDF-safe visual system used by
// the AI worksheet builder, the Practice page, and the Print page so every
// generated worksheet reads as one branded product: an illustrated header banner
// with mascots + math-symbol confetti, colored Roman-numeral section banners with
// star badges and per-section instruction ribbons, and a footer banner.
//
// Everything here is plain inline SVG + inline styles + Tailwind utilities so it
// survives html2canvas rasterization (worksheetImage.js) and the browser print
// path (Print.jsx) identically. No external images, no web fonts required to read.

// Brand palette (mirrors tailwind.config brand tokens; inlined so SVG fills match).
export const WS_COLORS = {
  blue: "#3B82F6",
  yellow: "#FACC15",
  coral: "#FB7185",
  green: "#22C55E",
  orange: "#F97316",
  purple: "#8B5CF6",
  ink: "#0A0A0A",
  paper: "#F9F9F6",
};

// Section banners cycle through these so consecutive sections are visually distinct.
const SECTION_PALETTE = ["blue", "green", "purple", "coral", "orange"];
export const sectionColor = (i) => WS_COLORS[SECTION_PALETTE[i % SECTION_PALETTE.length]];

const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];
export const toRoman = (i) => ROMAN[i] || String(i + 1);

// ── Mascots ──────────────────────────────────────────────────────────────────
function PencilMascot({ size = 74 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" style={{ flexShrink: 0 }} aria-hidden="true">
      <g transform="rotate(-18 50 50)">
        <rect x="38" y="14" width="24" height="56" rx="5" fill={WS_COLORS.yellow} stroke={WS_COLORS.ink} strokeWidth="3.5" />
        <rect x="38" y="30" width="24" height="7" fill={WS_COLORS.orange} stroke={WS_COLORS.ink} strokeWidth="2" />
        <polygon points="38,70 62,70 50,90" fill="#FDE9B0" stroke={WS_COLORS.ink} strokeWidth="3.5" />
        <polygon points="45.5,80 54.5,80 50,90" fill={WS_COLORS.ink} />
        <circle cx="45" cy="48" r="3" fill={WS_COLORS.ink} />
        <circle cx="55" cy="48" r="3" fill={WS_COLORS.ink} />
        <path d="M44 56 Q50 61 56 56" stroke={WS_COLORS.ink} strokeWidth="2.5" fill="none" strokeLinecap="round" />
        <circle cx="41" cy="54" r="2.5" fill={WS_COLORS.coral} opacity="0.7" />
        <circle cx="59" cy="54" r="2.5" fill={WS_COLORS.coral} opacity="0.7" />
      </g>
    </svg>
  );
}

function StarMascot({ size = 74 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" style={{ flexShrink: 0 }} aria-hidden="true">
      <polygon
        points="50,8 61,38 94,38 67,58 77,90 50,70 23,90 33,58 6,38 39,38"
        fill={WS_COLORS.yellow} stroke={WS_COLORS.ink} strokeWidth="3.5" strokeLinejoin="round"
      />
      <circle cx="43" cy="48" r="3.2" fill={WS_COLORS.ink} />
      <circle cx="57" cy="48" r="3.2" fill={WS_COLORS.ink} />
      <path d="M42 57 Q50 64 58 57" stroke={WS_COLORS.ink} strokeWidth="2.8" fill="none" strokeLinecap="round" />
      <circle cx="38" cy="55" r="3" fill={WS_COLORS.coral} opacity="0.7" />
      <circle cx="62" cy="55" r="3" fill={WS_COLORS.coral} opacity="0.7" />
    </svg>
  );
}

// A small filled star used as the badge on each section banner.
function StarBadge({ size = 26 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ flexShrink: 0 }} aria-hidden="true">
      <polygon
        points="12,2 15,9 22,9 16.5,13.5 18.5,21 12,16.5 5.5,21 7.5,13.5 2,9 9,9"
        fill={WS_COLORS.yellow} stroke={WS_COLORS.ink} strokeWidth="1.6" strokeLinejoin="round"
      />
    </svg>
  );
}

// Scattered translucent math symbols behind the header title.
const CONFETTI = [
  { s: "+", left: "11%", top: "22%", size: 30 },
  { s: "×", left: "29%", top: "66%", size: 22 },
  { s: "÷", left: "51%", top: "14%", size: 26 },
  { s: "=", left: "69%", top: "70%", size: 30 },
  { s: "−", left: "88%", top: "30%", size: 24 },
  { s: "★", left: "43%", top: "82%", size: 18 },
  { s: "★", left: "19%", top: "82%", size: 16 },
];

// ── Header banner ────────────────────────────────────────────────────────────
export function WorksheetHeader({ title, eyebrow, subtitle }) {
  return (
    <div
      style={{
        position: "relative",
        overflow: "hidden",
        border: `3px solid ${WS_COLORS.ink}`,
        borderRadius: 16,
        // Solid fallback under the gradient so the header never rasterizes blank
        // if html2canvas drops the gradient image.
        backgroundColor: WS_COLORS.blue,
        backgroundImage: `linear-gradient(135deg, #4f9bff 0%, ${WS_COLORS.blue} 55%, #2f6fe0 100%)`,
        boxShadow: `5px 5px 0 ${WS_COLORS.ink}`,
        padding: "18px 20px",
        marginBottom: 16,
      }}
    >
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }} aria-hidden="true">
        {CONFETTI.map((c, i) => (
          <span
            key={i}
            style={{
              position: "absolute", left: c.left, top: c.top, fontSize: c.size,
              fontWeight: 900, opacity: 0.28, color: "#fff",
              fontFamily: "Fredoka, sans-serif", lineHeight: 1,
            }}
          >
            {c.s}
          </span>
        ))}
      </div>
      <div style={{ position: "relative", zIndex: 2, display: "flex", alignItems: "center", gap: 14 }}>
        <PencilMascot />
        <div style={{ flex: 1, textAlign: "center", minWidth: 0 }}>
          {eyebrow && (
            <span
              className="font-heading"
              style={{
                display: "inline-block", background: WS_COLORS.yellow, border: `2.5px solid ${WS_COLORS.ink}`,
                color: WS_COLORS.ink, fontWeight: 800, fontSize: 12, letterSpacing: 0.4,
                padding: "2px 12px", borderRadius: 999, boxShadow: `2px 2px 0 ${WS_COLORS.ink}`,
                textTransform: "uppercase",
              }}
            >
              {eyebrow}
            </span>
          )}
          <div
            className="font-heading"
            style={{
              color: "#fff", fontSize: 28, fontWeight: 800, lineHeight: 1.3, margin: "12px 0 10px",
              textShadow: "2.5px 2.5px 0 rgba(10,10,10,.55)",
            }}
          >
            {title}
          </div>
          {subtitle && (
            <span
              style={{
                display: "inline-block", background: "#fff", border: `2.5px solid ${WS_COLORS.ink}`,
                borderRadius: 999, padding: "2px 14px", fontWeight: 800, color: WS_COLORS.ink,
                fontSize: 13, boxShadow: `2px 2px 0 ${WS_COLORS.ink}`,
              }}
            >
              {subtitle}
            </span>
          )}
        </div>
        <StarMascot />
      </div>
    </div>
  );
}

// ── Instructions ribbon (top-of-sheet, with an exclamation pin) ──────────────
export function InstructionRibbon({ children }) {
  if (!children) return null;
  return (
    <div
      style={{
        display: "flex", gap: 10, alignItems: "flex-start", background: WS_COLORS.paper,
        border: `2.5px solid ${WS_COLORS.ink}`, borderRadius: 12, padding: "10px 14px",
        marginBottom: 14, boxShadow: "3px 3px 0 rgba(10,10,10,.18)", fontSize: 13.5, fontWeight: 600,
      }}
    >
      <svg width="22" height="22" viewBox="0 0 24 24" style={{ flexShrink: 0, marginTop: 1 }} aria-hidden="true">
        <circle cx="12" cy="12" r="10" fill={WS_COLORS.coral} stroke={WS_COLORS.ink} strokeWidth="2" />
        <path d="M12 7v6" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" />
        <circle cx="12" cy="16.5" r="1.4" fill="#fff" />
      </svg>
      <div>{children}</div>
    </div>
  );
}

// ── Section banner (colored bar + star badge + Roman title + points pill) ────
// plus an optional dashed per-section instruction ribbon below it.
export function SectionBanner({ index = 0, title, points, instructions }) {
  const bg = sectionColor(index);
  return (
    <>
      <div
        className="break-after-avoid"
        style={{
          display: "flex", alignItems: "center", gap: 10, border: `3px solid ${WS_COLORS.ink}`,
          borderRadius: 12, padding: "7px 14px", boxShadow: `4px 4px 0 ${WS_COLORS.ink}`,
          color: "#fff", background: bg,
        }}
      >
        <StarBadge />
        <span
          className="font-heading"
          style={{ fontSize: 18, fontWeight: 800, flex: 1, textShadow: "1.5px 1.5px 0 rgba(10,10,10,.45)" }}
        >
          {toRoman(index)}. {title}
        </span>
        {points != null && (
          <span
            style={{
              fontSize: 12, fontWeight: 800, background: "#fff", color: WS_COLORS.ink,
              border: `2px solid ${WS_COLORS.ink}`, borderRadius: 999, padding: "1px 9px",
              boxShadow: `1.5px 1.5px 0 ${WS_COLORS.ink}`, whiteSpace: "nowrap",
            }}
          >
            {points} {points === 1 ? "pt" : "pts"}
          </span>
        )}
      </div>
      {instructions && (
        <div
          style={{
            fontSize: 12.5, fontWeight: 700, color: "#333", background: WS_COLORS.paper,
            border: "2px dashed rgba(10,10,10,.3)", borderRadius: 9, padding: "6px 12px",
            margin: "8px 0 12px",
          }}
        >
          {instructions}
        </div>
      )}
    </>
  );
}

// ── Footer banner ────────────────────────────────────────────────────────────
export function WorksheetFooter({ label = "iLoveMathWorksheets.com" }) {
  return (
    <div
      className="font-heading"
      style={{
        marginTop: 20, textAlign: "center", background: WS_COLORS.yellow,
        border: `3px solid ${WS_COLORS.ink}`, borderRadius: 12, padding: 7,
        fontWeight: 800, fontSize: 14, boxShadow: `4px 4px 0 ${WS_COLORS.ink}`,
      }}
    >
      ★ {label} ★
    </div>
  );
}

// Sensible default per-section instructions keyed by the AI pipeline's type ids,
// so each section reads like a real test even when the model omits instructions.
export const SECTION_INSTRUCTIONS = {
  "multiple-choice": "Choose the letter of the correct answer.",
  "true-false": "Write TRUE if the statement is correct and FALSE if it is wrong.",
  "always-sometimes-never": "Write ALWAYS, SOMETIMES, or NEVER for each statement.",
  "fill-in-the-blank": "Write the correct answer on the blank.",
  "problem-solving": "Show your solution and box your final answer.",
  "mathematical-calculations": "Solve each item. Show your complete solution.",
  "matching-type": "Match Column A with its correct description in Column B. Write the letter of the answer.",
  "essay": "Answer in complete sentences.",
};
